import { useMemo } from 'react';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { useSelection } from '@spazious/dollhouse-engine';
import { CameraPathNode, FNodeTypesEnum, getNodeFromObject } from '@spazious/dollhouse-fsetup';
import { Translate } from '@spazious/dollhouse-i18n';
import { useStaging } from '@spazious/dollhouse-store';
import { Flex, InputNumber, Typography } from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

import { Grid } from '../InspectorPanel/Grid.js';

export const CamerapathPanel = () => {
  const selected = useSelection(({ selected }) => selected);
  const { createAction, staging } = useStaging();

  const handleCamHeightChange = useEventCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    if (!cameraPathNode) {
      return;
    }
    if (!evt.target.checkValidity()) {
      return;
    }
    const newVal = parseFloat(evt.target.value);
    const newPoints = cameraPathNode.points.map(([p0, p1]) => [[p0[0], newVal, p0[2]], p1]);
    staging.add(createAction('update', cameraPathNode?.selector!, 'points', newPoints));
  });

  const handleCamTargetChange = useEventCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    if (!cameraPathNode) {
      return;
    }
    if (!evt.target.checkValidity()) {
      return;
    }
    const newVal = parseFloat(evt.target.value);
    const newPoints = cameraPathNode.points.map(([p0, p1]) => [p0, [p1[0], newVal, p1[2]]]);
    staging.add(createAction('update', cameraPathNode?.selector!, 'points', newPoints));
  });

  const handleBlur = useEventCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    if (!evt.target.checkValidity()) {
      evt.target.reportValidity();
      if (staging.stack.length) {
        staging.abort();
      }
      return;
    }
    if (staging.stack.length) {
      staging.commit(CommanderActionTypesEnum.Update);
    }
  });

  const cameraPathNode: CameraPathNode | null = useMemo(() => {
    if (selected.length !== 1) return null;
    const node = getNodeFromObject(selected[0]);
    return node?.type === FNodeTypesEnum.CameraPath ? node : null;
  }, [selected]);

  const format = (value: number | string) => '' + value;

  if (!cameraPathNode) return null;

  return (
    <Flex column padding="var(--spacing-gap)">
      <Grid>
        <Typography
          size="large"
          style={{
            padding: 8,
            color: 'gray',
            userSelect: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title="Camera Height"
          variant="label"
        >
          <Translate>Camera Height</Translate>
        </Typography>
        <Flex alignItems="center" gap="calc(2 * var(--spacing-gap))">
          <InputNumber
            key="camera-height"
            format={format}
            max={100}
            min={1.81}
            step={0.01}
            title="Camera Height"
            value={cameraPathNode.points[0][0][1]}
            onBlur={handleBlur}
            onChange={handleCamHeightChange}
          />
        </Flex>
        <Typography
          size="large"
          style={{
            padding: 8,
            // paddingLeft: 0,
            color: 'gray',
            userSelect: 'none',
            // maxWidth: '120px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title="Target Height"
          variant="label"
        >
          <Translate>Target Height</Translate>
        </Typography>
        <Flex alignItems="center" gap="calc(2 * var(--spacing-gap))">
          <InputNumber
            key="target-height"
            format={format}
            max={100}
            min={0}
            step={0.01}
            title="Target Height"
            value={cameraPathNode.points[0][1][1]}
            onBlur={handleBlur}
            onChange={handleCamTargetChange}
          />
        </Flex>
      </Grid>
    </Flex>
  );
};
